import { useEffect, useState } from 'react'
import { BookmarkPlus, LayoutTemplate, X } from 'lucide-react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import {
  applyTemplate,
  templateFromBlocks,
  templatesFor,
  type CampaignTemplate,
} from '@iws/theme-schema/campaign'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { deleteCampaignTemplate, loadCampaignTemplates, saveCampaignTemplate } from '@/lib/campaign-templates'
import { OfferModel } from '@/types'

interface Props {
  offerType: OfferModel.OfferType
  blocks: OfferModel.CampaignBlock[]
  onApply: (blocks: OfferModel.CampaignBlock[]) => void
}

/** Start the page from a template (FR-CB6), or save this page as a template for later campaigns. */
export const TemplatePicker = ({ offerType, blocks, onApply }: Props) => {
  const [saved, setSaved] = useState<CampaignTemplate[]>([])
  const [confirming, setConfirming] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    loadCampaignTemplates().then(setSaved).catch(() => toast.error('Could not load your saved templates'))
  }, [])
  const templates = templatesFor(offerType, saved)

  const apply = (t: CampaignTemplate) => {
    if (blocks.length && confirming !== t.id) {
      setConfirming(t.id)
      return
    }
    onApply(applyTemplate(t, uuidv4) as OfferModel.CampaignBlock[])
    setConfirming(null)
    toast.success(`Page started from “${t.label}”`)
  }

  const save = async () => {
    if (!name.trim() || !blocks.length) return
    setBusy(true)
    try {
      setSaved(await saveCampaignTemplate(templateFromBlocks(name.trim(), offerType, blocks)))
      setName('')
      toast.success('Template saved for your store')
    } catch {
      toast.error('Could not save the template')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    try {
      setSaved(await deleteCampaignTemplate(id))
    } catch {
      toast.error('Could not delete the template')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LayoutTemplate className="size-4 text-text-2" aria-hidden /> Start from a template
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          {templates.map((t) => (
            <div key={t.id} className="relative">
              <button
                type="button"
                onClick={() => apply(t)}
                className="w-full rounded-lg border border-border p-3 pr-9 text-left outline-none transition-colors hover:bg-subdued focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
                  {t.label}
                  {t.offerType === offerType && !t.id.startsWith('saved-') && <Badge tone="info">Recommended</Badge>}
                </span>
                <span className="mt-0.5 block text-sm text-text-2">
                  {confirming === t.id ? 'Click again to replace the current page' : t.description}
                </span>
              </button>
              {t.id.startsWith('saved-') && (
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Delete template ${t.label}`}
                  onClick={() => remove(t.id)}
                  className="absolute right-2 top-2 text-muted-foreground hover:text-ink"
                >
                  <X />
                </Button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 border-t border-border-subtle pt-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name this page to reuse it"
            aria-label="Template name"
          />
          <Button variant="secondary" onClick={save} disabled={busy || !name.trim() || !blocks.length}>
            <BookmarkPlus /> Save as template
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
